import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../api";
import {
  ErrorBanner,
  Field,
  formatTime,
  inputClass,
  Panel,
  PrimaryButton,
  SecondaryButton,
} from "./ui";

type TownForm = {
  name: string;
  announcement: string;
  status: string;
  timeScale: number;
  taxRate: number;
};

const EMPTY_FORM: TownForm = {
  name: "",
  announcement: "",
  status: "active",
  timeScale: 1,
  taxRate: 0,
};

export default function AdminTowns() {
  const navigate = useNavigate();
  const { townId } = useParams();
  const [towns, setTowns] = useState<any[]>([]);
  const [mayors, setMayors] = useState<any[]>([]);
  const [detail, setDetail] = useState<any>(null);
  const [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [create, setCreate] = useState({ name: "", slug: "", ownerUserId: "", maxResidents: "40" });
  const [form, setForm] = useState<TownForm>(EMPTY_FORM);
  const [assignOwner, setAssignOwner] = useState("");
  const [walletDelta, setWalletDelta] = useState("1000");
  const [walletMemo, setWalletMemo] = useState("超管调整");

  const load = async () => {
    const [townResult, mayorResult] = await Promise.all([api.adminTowns(), api.adminMayors()]);
    setTowns(townResult.towns);
    setMayors(mayorResult.mayors);
    return townResult.towns;
  };

  const openTown = async (townId: string) => {
    setSelectedId(townId);
    setMessage("");
    const next = await api.adminTown(townId);
    setDetail(next);
    setForm({
      name: next.town.name,
      announcement: next.town.announcement,
      status: next.town.status,
      timeScale: next.town.timeScale,
      taxRate: next.town.taxRate,
    });
    setAssignOwner(next.town.ownerUserId || "");
  };

  useEffect(() => {
    let cancelled = false;
    load().then((items: any[]) => {
      const target = townId ? items.find((town) => town.id === townId) : items[0];
      if (target && !cancelled) void openTown(target.id);
      if (townId && !target && !cancelled) setError("指定的小镇不存在");
    }).catch((e) => {
      if (!cancelled) setError(e.message);
    });
    return () => { cancelled = true; };
  }, [townId]);

  const createTown = async () => {
    setError(""); setMessage("");
    try {
      const maxResidents = Number(create.maxResidents);
      const result = await api.adminCreateTown({
        name: create.name.trim(),
        slug: create.slug.trim().toLowerCase(),
        ownerUserId: create.ownerUserId || null,
        config: Number.isFinite(maxResidents) ? { maxResidents } : {},
      });
      setCreate({ name: "", slug: "", ownerUserId: "", maxResidents: "40" });
      setMessage("小镇已创建，并已发放创世基金。");
      await load();
      await openTown(result.town.id);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const save = async () => {
    if (!selectedId) return;
    setError(""); setMessage("");
    try {
      await api.adminUpdateTown(selectedId, form);
      setMessage("小镇设置已保存。");
      await load();
      await openTown(selectedId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const assign = async () => {
    if (!selectedId) return;
    setError(""); setMessage("");
    try {
      await api.adminAssignTown(selectedId, assignOwner || null);
      setMessage("城主归属已更新。");
      await load();
      await openTown(selectedId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const adjustWallet = async () => {
    if (!selectedId) return;
    setError(""); setMessage("");
    try {
      const result = await api.adminAdjustWallet(selectedId, Number(walletDelta), walletMemo);
      setMessage(`余额已调整为 ${Number(result.balance).toLocaleString()}。`);
      await load();
      await openTown(selectedId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const closeTown = async () => {
    if (!selectedId || !window.confirm("关闭后小镇将停止运行并保留数据，确认继续？")) return;
    setError(""); setMessage("");
    try {
      await api.adminCloseTown(selectedId);
      setMessage("小镇已关闭。");
      await load();
      await openTown(selectedId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const statusLabel: Record<string, string> = {
    active: "活跃",
    slow: "慢速",
    sleeping: "沉睡",
    closed: "关闭",
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-black">小镇管理</h1>
        <p className="mt-1 text-sm text-[#77705f]">创建小镇、分配城主并调整运行参数与钱包。</p>
      </div>
      <ErrorBanner message={error} />
      {message && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{message}</div>}

      <Panel title="创建小镇">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5 xl:items-end">
          <Field label="镇名">
            <input className={inputClass} value={create.name} onChange={(e) => setCreate({ ...create, name: e.target.value })} placeholder="云端花园" />
          </Field>
          <Field label="slug">
            <input className={inputClass} value={create.slug} onChange={(e) => setCreate({ ...create, slug: e.target.value })} placeholder="cloud-garden" />
          </Field>
          <Field label="城主">
            <select className={inputClass} value={create.ownerUserId} onChange={(e) => setCreate({ ...create, ownerUserId: e.target.value })}>
              <option value="">平台直营</option>
              {mayors.map((mayor) => <option key={mayor.userId} value={mayor.userId}>{mayor.username}（{mayor.email}）</option>)}
            </select>
          </Field>
          <Field label="居民上限">
            <input type="number" min="0" className={inputClass} value={create.maxResidents} onChange={(e) => setCreate({ ...create, maxResidents: e.target.value })} />
          </Field>
          <PrimaryButton disabled={!create.name.trim() || !create.slug.trim()} onClick={createTown}>创建小镇</PrimaryButton>
        </div>
      </Panel>

      <Panel title={`小镇列表（${towns.length}）`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] text-left text-sm">
            <thead className="border-b border-[#e4dfcf] text-[#77705f]">
              <tr><th className="py-2">名称</th><th>城主</th><th>状态</th><th>时钟</th><th>流速</th><th>在线</th><th>钱包</th><th /></tr>
            </thead>
            <tbody>
              {towns.map((town) => (
                <tr key={town.id} className={`border-b border-[#f0ecdf] ${town.status === "closed" ? "text-[#999]" : ""}`}>
                  <td className="py-3">
                    <div className="font-bold">{town.name}</div>
                    <code className="text-xs text-[#8b8471]">{town.slug}</code>
                  </td>
                  <td>{town.ownerUsername || "平台直营"}</td>
                  <td>{statusLabel[town.status] || town.status}</td>
                  <td>第 {town.day} 天 {String(Math.floor(town.gameMin / 60)).padStart(2, "0")}:{String(town.gameMin % 60).padStart(2, "0")}</td>
                  <td>{town.timeScale}x</td>
                  <td>{town.online}</td>
                  <td>{Number(town.tokenBalance).toLocaleString()}</td>
                  <td className="text-right">
                    <SecondaryButton onClick={() => navigate(`/admin/towns/${encodeURIComponent(town.id)}`)}>管理</SecondaryButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {detail && (
        <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
          <Panel title={`${detail.town.name} · 设置`}>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="镇名"><input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="状态">
                <select className={inputClass} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="active">活跃</option><option value="slow">慢速</option>
                  <option value="sleeping">沉睡</option><option value="closed">关闭</option>
                </select>
              </Field>
              <Field label="流速">
                <select className={inputClass} value={form.timeScale} onChange={(e) => setForm({ ...form, timeScale: Number(e.target.value) })}>
                  {[0.2, 1, 2, 3].map((scale) => <option key={scale} value={scale}>{scale}x</option>)}
                </select>
              </Field>
              <Field label="税率">
                <input type="number" min="0" max="0.3" step="0.01" className={inputClass}
                  value={form.taxRate} onChange={(e) => setForm({ ...form, taxRate: Number(e.target.value) })} />
              </Field>
              <div className="md:col-span-2">
                <Field label="镇公告">
                  <textarea rows={3} className={inputClass} value={form.announcement}
                    onChange={(e) => setForm({ ...form, announcement: e.target.value })} />
                </Field>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <PrimaryButton onClick={save}>保存设置</PrimaryButton>
              {detail.town.id !== "town_default" && detail.town.status !== "closed" &&
                <button onClick={closeTown} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-bold text-white">关闭小镇</button>}
            </div>
          </Panel>

          <div className="space-y-5">
            <Panel title="城主归属">
              <Field label="选择城主">
                <select className={inputClass} value={assignOwner} onChange={(e) => setAssignOwner(e.target.value)}>
                  <option value="">平台直营</option>
                  {mayors.map((mayor) => <option key={mayor.userId} value={mayor.userId}>{mayor.username}（{mayor.email}）</option>)}
                </select>
              </Field>
              <div className="mt-3"><PrimaryButton onClick={assign}>确认分配</PrimaryButton></div>
            </Panel>

            <Panel title="钱包调整">
              <div className="text-2xl font-black">{Number(detail.town.tokenBalance).toLocaleString()} <span className="text-sm font-normal text-[#77705f]">token</span></div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <Field label="变动值"><input type="number" className={inputClass} value={walletDelta} onChange={(e) => setWalletDelta(e.target.value)} /></Field>
                <Field label="备注"><input className={inputClass} value={walletMemo} onChange={(e) => setWalletMemo(e.target.value)} /></Field>
              </div>
              <div className="mt-3"><PrimaryButton disabled={!Number(walletDelta)} onClick={adjustWallet}>确认调整</PrimaryButton></div>
            </Panel>
          </div>
        </div>
      )}

      {detail && (
        <div className="grid gap-5 xl:grid-cols-2">
          <Panel title="近 7 日用量与成员">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-[#f7f5ed] p-3"><div className="text-xs text-[#77705f]">调用次数</div><div className="text-xl font-black">{detail.usage7d.calls}</div></div>
              <div className="rounded-lg bg-[#f7f5ed] p-3"><div className="text-xs text-[#77705f]">总 tokens</div><div className="text-xl font-black">{Number(detail.usage7d.totalTokens).toLocaleString()}</div></div>
              <div className="rounded-lg bg-[#f7f5ed] p-3"><div className="text-xs text-[#77705f]">成员</div><div className="text-xl font-black">{detail.town.memberCount}</div></div>
            </div>
            <div className="mt-4 space-y-2">
              {detail.members.slice(0, 8).map((member: any) => (
                <div key={member.userId} className="flex justify-between rounded-md border border-[#ece7d8] px-3 py-2 text-sm">
                  <span>{member.username}</span><span className={member.isBanned ? "text-red-700" : "text-[#77705f]"}>{member.isBanned ? "已封禁" : member.role}</span>
                </div>
              ))}
              {!detail.members.length && <p className="text-sm text-[#918a78]">暂无成员。</p>}
            </div>
          </Panel>

          <Panel title="最近钱包流水">
            <div className="space-y-2">
              {detail.ledger.map((entry: any) => (
                <div key={entry.id} className="grid grid-cols-[1fr_auto] gap-2 rounded-md border border-[#ece7d8] px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium">{entry.kind} · {entry.memo || "-"}</div>
                    <div className="text-xs text-[#8b8471]">{formatTime(entry.createdAt)} / 余额 {Number(entry.balanceAfter).toLocaleString()}</div>
                  </div>
                  <div className={Number(entry.delta) >= 0 ? "font-bold text-emerald-700" : "font-bold text-red-700"}>
                    {Number(entry.delta) >= 0 ? "+" : ""}{Number(entry.delta).toLocaleString()}
                  </div>
                </div>
              ))}
              {!detail.ledger.length && <p className="text-sm text-[#918a78]">暂无流水。</p>}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
